/**
 * MonoCodeChip — inline monospace pill for command/path/ARN snippets in
 * chrome surfaces (status bar tags, audit row callouts, agent tool names).
 *
 * Wayne-specific concept (mono token + optional copy affordance + hairline
 * border that adapts to the active surface). Custom atom — does not wrap
 * Cloudscape's `Box` because the styling rules here are stricter (no
 * inheritable `color`, no padding override).
 *
 * Keeps the surface ratio readable in both Forest light + Forest dark; the
 * border is hairline so dense rows don't look fenced.
 */
import { type CSSProperties, useState } from 'react';

export interface MonoCodeChipProps {
  readonly children: string;
  /** Show a copy button on the right edge. */
  readonly copyable?: boolean;
  /** Compact variant for inline-with-text use. */
  readonly compact?: boolean;
  /** Optional override for the rendered ARIA label. */
  readonly ariaLabel?: string;
}

export default function MonoCodeChip({
  children,
  copyable = false,
  compact = false,
  ariaLabel,
}: MonoCodeChipProps): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permission denied — silently no-op. We do not surface a
      // "couldn't copy" toast because the value is still visible.
      setCopied(false);
    }
  };

  const padY = compact ? 1 : 2;
  const padX = compact ? 4 : 6;
  const fontSize = compact ? 12 : 13;

  const chip: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: `${padY}px ${padX}px`,
    borderRadius: 4,
    background: 'var(--wayne-surface)',
    border: '1px solid var(--wayne-hairline)',
    color: 'var(--wayne-ink)',
    fontFamily:
      '"Söhne Mono", "Amazon Ember Mono", "JetBrains Mono", monospace',
    fontSize,
    lineHeight: 1.4,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const button: CSSProperties = {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: 'var(--wayne-ink-3)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    padding: 0,
    lineHeight: 1,
    flex: '0 0 auto',
  };

  return (
    <code style={chip} aria-label={ariaLabel ?? children} title={children}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
        {children}
      </span>
      {copyable ? (
        <button
          type="button"
          onClick={handleCopy}
          style={button}
          aria-label={copied ? 'Copied' : `Copy ${children}`}
        >
          {copied ? '✓' : '⧉'}
        </button>
      ) : null}
    </code>
  );
}
